import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatSnackBarModule,
        NgClass,
        NgTemplateOutlet,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;

    private _overlayRef: OverlayRef;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
        private _snackBar: MatSnackBar,
    ) {}

    ngOnInit(): void {
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.unreadCount = count;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.newNotification$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notification: Notification) => this._showPopup(notification));
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        if (this._overlayRef) this._overlayRef.dispose();
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) return;
        if (!this._overlayRef) this._createOverlay();
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
        this._notificationsService.sync(true).subscribe(); // re-sync read / cleared state of the visible list
    }

    closePanel(): void {
        this._overlayRef?.detach();
    }

    markAllAsRead(): void {
        this._notificationsService.markAllAsRead();
    }

    markRead(notification: Notification): void {
        this._notificationsService.markRead(notification);
    }

    delete(notification: Notification): void {
        this._notificationsService.delete(notification);
    }

    /** Mark read, then open the link (FE-22: the email on the Team dashboard / My mail). */
    open(notification: Notification): void {
        this._notificationsService.markRead(notification);
        if (notification.link) this._router.navigateByUrl(notification.link);
        this.closePanel();
    }

    private _showPopup(notification: Notification): void {
        const ref = this._snackBar.open(notification.title || 'New notification', notification.link ? 'View' : 'Dismiss', {
            duration: 12_000,
            horizontalPosition: 'end',
            verticalPosition: 'bottom',
        });
        if (notification.link) ref.onAction().subscribe(() => this.open(notification));
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top' },
                    { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom' },
                ]),
        });
        this._overlayRef.backdropClick().subscribe(() => this._overlayRef.detach());
    }
}
